import { INPUTS_READERS } from 'src/constants';
import { _init_exchange_based_conditions } from 'src/components/Scenes/helpers/init_exchange_based_conditions';
import { percent_from_value_range } from 'src/helpers/utilities';
import { shellyId16 } from 'src/helpers/interface_helpers';
import {
	extractVCProperty,
	interpretIotsVCProperty,
	isVCType,
} from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';
import type { DeviceID } from 'src/types/DeviceID';
import {
	extractZoneIdFromPresenceCondition,
	isPresenceZoneCondition,
	getPresenceConditionType,
	validatePresenceConditionData,
	normalizePresenceConditionValue,
} from '../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
import { parseCameraSceneCommand } from '../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import {
	isCameraSceneActionCommand,
	parseCameraSceneActionCommand,
} from 'src/components/Scenes/helpers/camera_action_helpers';
import {
	VENTORA_FAN_SCENE_UI,
	ventoraFanOnCloudToUi,
} from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';

export const extractAddOnId = (sceneCommand) => {
	switch (sceneCommand) {
		case 'h1':
		case 't1':
			return 0;
		case 't2':
			return 1;
		case 't3':
			return 2;
		default:
			return undefined;
	}
};

/**
 * Extracts the device channel number from a Scene Service command string.
 *
 * The function first attempts to extract the device channel (numeric value) from the input string.
 * If the extracted number is
 *  - greater than 2000: this means that the channel is a blu TRV dynamic channel)
 *  - greater than 200: this means that the channel is a virtual component channel)
 * and the function returns that number.
 * Otherwise, it matches the input string against a set of predefined cases (e.g. 'r1', 'r2', 'r3', etc.)
 * and returns the corresponding channel number.
 *
 * Note: definitely a candidate for refactor for parsing the channel number.
 *
 * @param {string} sceneCommand - The Scene Service command string from which to extract the device channel.
 * @returns {number} The extracted device channel number. Returns 0 if no match is found.
 */
export const extractDeviceChannel = (sceneCommand) => {
	const match = sceneCommand.match(/\d+/);
	const number = match ? parseInt(match[0], 10) : 0;
	const vcCondition = extractVCProperty(sceneCommand);

	//Extracting channel:
	//  200+ for virtual components;
	//  2000+ for Blu TRV
	if (number > 2000 || (number > 200 && vcCondition?.condition_name)) {
		// if (import.meta.env.DEV) {
		// 	console.log('VC/blutrv channel:', number);
		// }
		return number;
	}

	switch (sceneCommand) {
		case 'r2':
		case 'brightness2':
		case 'i2':
		case 'ie2':
		case 'ie2c':
		case 'ic2':
		case 'm2':
		case 'v2':
		case 'rl2':
		case 'rlp2':
		case 'rbp2':
		case 'ct2':
			return 1;

		case 'r3':
		case 'brightness3':
		case 'i3':
		case 'ie3':
		case 'ic3':
		case 'm3':
		case 'v3':
		case 'ct3':
			return 2;

		case 'i4':
		case 'ie4':
		case 'm4':
		case 'r4':
		case 'brightness4':
		case 'v4':
		case 'ct4':
			return 3;

		case 'i5':
		case 'ie5':
		case 'm5':
		case 'r5':
		case 'brightness5':
			return 4;

		case 'i6':
		case 'ie6':
		case 'm6':
		case 'r6':
		case 'brightness6':
			return 5;

		case 'i7':
		case 'ie7':
		case 'm7':
		case 'r7':
		case 'brightness7':
			return 6;

		case 'i8':
		case 'ie8':
		case 'm8':
		case 'r8':
		case 'brightness8':
			return 7;
		case 'temp_enable':
		case 'set_temp': //This command is from wall display's action scene
			return 101;

		case 'Radio_PlayFavourite':
		// case 'Radio_Pause'//not implemented yet
		case 'Radio_Stop':
		case 'MP_PlayAudioClip':
		case 'MP_Play':
		case 'MP_Pause':
		case 'MP_Stop':
			//Wall Display's media player
			return 100;

		default:
			return 0;
	}
};

/**
 * Processes the given condition string and returns an object with a standardized name and value  for the Blu TRV.
 *
 * @param sceneCommand - The Scene Service command string to be processed. The channel suffix (Non-alphabetic characters) will be removed.
 * @param tshvalue - The value associated with the condition.
 * @returns An object containing the standardized name and the provided value.
 *
 * The function handles the following iots string conditions, all blu TRV specific:
 * - 'boost': Returns an object with name 'boost' and the provided value.
 * - 'sch': Returns an object with name 'set_profile' and the provided value.
 * - 'tgt': Returns an object with name 'trv_target_temperature' and the provided value.
 * - 'pos': Returns an object with name 'valve_percent_position' and the provided value.
 * - 'isopen': Returns an object with name 'valve_open_state' and the provided value.
 * - 'rep': Returns an object with name 'reported_by' and the provided value.
 * - For any other condition, returns an object with the original condition string as the name and the provided value.
 */
function handleBluTRVConditions(sceneCommand, tshvalue, channel) {
	const condition = sceneCommand.replace(/[^a-zA-Z]/g, '');
	// all channels in the iots spec are 1-based, so we need to decrement the channel number by 1
	// candidate for refactor as there is a lot of same parameter passing and returning
	channel = channel - 1;
	switch (condition) {
		case 'boost':
			return { name: 'boost', value: tshvalue, channel };
		case 'sch':
			return { name: 'set_profile', value: tshvalue, channel };
		case 'tgt':
			return { name: 'trv_target_temperature', value: tshvalue, channel };
		case 'pos':
			return { name: 'valve_percent_position', value: tshvalue, channel };
		case 'isopen':
			return {
				name: 'valve_open_state',
				value: tshvalue === 'on' ? 'open' : 'closed',
				channel,
			};
		case 'rep':
			return { name: 'reported_by', value: tshvalue, channel };
		case 'tmp':
			return { name: 'temperature', value: tshvalue, channel };
		case 'bat':
			return { name: 'battery', value: Number(tshvalue), channel };
		default:
			return { name: sceneCommand, value: tshvalue, channel };
	}
}

/**
 * Converts Scene Service command to UI property format
 * @param sceneCommand - Scene Service command/property (e.g., 'r1', 'brightness2', 't1')
 * @param tshvalue - Optional threshold value
 * @returns Object with name, value, channel, and other UI-friendly properties
 */
export const convertSceneCommandToUiProperty = (sceneCommand, tshvalue = undefined) => {
	// Camera scene actions: write-only tokens whose `tshvalue` is the `set` object.
	if (isCameraSceneActionCommand(sceneCommand)) {
		const cameraAction = parseCameraSceneActionCommand(sceneCommand, tshvalue);
		if (cameraAction) {
			return cameraAction;
		}
	}

	const cameraCondition = parseCameraSceneCommand(sceneCommand, tshvalue);
	if (cameraCondition) {
		return cameraCondition;
	}

	const channel = extractDeviceChannel(sceneCommand);

	// 2000 is the starting channel number for Blu TRV devices
	if (channel > 2000) {
		return { ...handleBluTRVConditions(sceneCommand, tshvalue, channel) };
	} else if (channel > 200) {
		//Virtual component as condition
		return { ...interpretIotsVCProperty(sceneCommand, tshvalue) };
	}

	switch (sceneCommand) {
		case 'adc1':
			return { name: 'adc', value: tshvalue, channel };
		case 'door':
		case 'window':
			return {
				name: sceneCommand,
				value: tshvalue === 'on' ? 'open' : 'close',
				channel,
			};

		case 'turn0':
		case 'turn1':
		case 'turn2':
		case 'turn3':
		case 'turn4':
		case 'turn5':
		case 'turn6':
		case 'turn7':
		case 'turn8':
		case 'r1':
		case 'r2':
		case 'r3':
		case 'r4':
		case 'r5':
		case 'r6':
		case 'r7':
		case 'r8': {
			// Scene uses "0"/"1", UI uses "on"/"off"
			const normalized = String(tshvalue).toLowerCase();
			const uiValue =
				normalized === '1' || normalized === 'true' || normalized === 'on'
					? 'on'
					: normalized === '0' || normalized === 'false' || normalized === 'off'
						? 'off'
						: tshvalue;
			return {
				name: 'turn',
				value: uiValue,
				channel,
			};
		}
		case 'state': {
			// because of color bulbs turn state; scene uses "0"/"1", UI uses "on"/"off"
			const normalized = String(tshvalue).toLowerCase();
			const uiValue =
				normalized === '1' || normalized === 'true' || normalized === 'on'
					? 'on'
					: normalized === '0' || normalized === 'false' || normalized === 'off'
						? 'off'
						: tshvalue;
			return {
				name: 'state',
				value: uiValue,
				channel,
			};
		}
		case 'brightness1':
		case 'brightness2':
		case 'brightness3':
		case 'brightness4':
		case 'brightness5':
		case 'brightness6':
		case 'brightness7':
		case 'brightness8':
		case 'gain':
			return {
				name: 'brightness',
				value: Number(tshvalue),
				channel,
			};
		case 'ct1':
		case 'ct2':
		case 'ct3':
		case 'ct4':
			return {
				name: 'ct',
				value: Number(tshvalue),
				channel,
			};

		case 'rl':
			return {
				name: 'roller state',
				value: tshvalue,
				channel,
			};
		case 'rld':
			return {
				name: 'roller direction',
				value: tshvalue,
				channel,
			};

		case 'rl1':
		case 'rl2':
			return {
				name: 'multi_cover_state',
				value: tshvalue,
				channel,
			};
		case 'rlp1':
		case 'rlp2':
			return {
				name: 'multi_cover_position',
				value: tshvalue,
				channel,
			};
		case 'rlp':
			return {
				name: 'roller position',
				value: Number(tshvalue),
				channel,
			};
		case 'rbp':
			return {
				name: 'blinds_tilt_position',
				value: Number(tshvalue),
				channel,
			};
		case 'rbp1':
		case 'rbp2':
			return {
				name: 'multi_cover_blinds_tilt_position',
				value: Number(tshvalue),
				channel,
			};
		case 'lux':
			return {
				name: 'luxury',
				value: tshvalue,
				channel,
			};
		case 'lux_i':
			return {
				name: 'illumination',
				value: Number(tshvalue),
				channel,
			};
		// case 'illumination':
		//     return {
		//         name: 'illumination',
		//         value: tshvalue,
		//         channel
		//     }
		case 'vibr':
			return {
				name: 'vibration',
				value: tshvalue === 'on' ? 'detected' : 'not detected',
				channel,
			};
		case 'motion':
			return {
				name: 'motion',
				value: tshvalue === 'on' ? 'detected' : 'not detected',
				channel,
			};
		case 'pir':
			return {
				name: 'pir',
				value: tshvalue === 'on' ? 'detected' : 'not detected',
				channel,
			};

		case 'tilt':
			return {
				name: 'tilt',
				value: Number(tshvalue),
				channel,
			};

		case 'smoke':
		case 'flood':
		case 'moisture_alarm':
			return {
				name: sceneCommand,
				value: ['true', 1, true].includes(tshvalue) ? 'detected' : 'not detected',
				channel,
			};
		case 'i1':
		case 'i2':
		case 'i3':
		case 'i4':
		case 'i5':
		case 'i6':
		case 'i7':
		case 'i8':
			return {
				name: 'input_value',
				value: tshvalue,
				channel,
				input_index: channel,
				//add_info_ind: channel
			};
		case 'ie1':
		case 'ie2':
		case 'ie3':
		case 'ie4':
		case 'ie5':
		case 'ie6':
		case 'ie7':
		case 'ie8':
		case 'ie1c':
		case 'ie2c' /**
		 * Remote control button press handling (SBRC-005B specific)
		 *
		 * Standard format (ie1, ie2): Button press on input 1 or 2
		 * Channel-specific format (ie1c, ie2c): Press with channel targeting
		 *
		 * For channel-encoded values (e.g., "S1", "S2"):
		 * - The numeric suffix indicates the remote control's output channel
		 * - The letter prefix indicates press type (S=short, L=long, etc.)
		 * - We extract the channel and default the push type to short press
		 * - The actual push type mapping comes from the device's input configuration
		 */: {
			const CHANNEL_PREFIX = 'channel_';
			const PUSH_TYPE_PREFIX_SHORT = 'S';
			const CONDITION_PREFIX_CHANNEL = 'c';
			const inputReader = (pushValue) => {
				return INPUTS_READERS.find((el) => el.check === pushValue)?.ui || '';
			};

			let remoteControlChannel;
			let pushValue = inputReader(tshvalue);
			if (
				sceneCommand.endsWith(CONDITION_PREFIX_CHANNEL) &&
				typeof tshvalue === 'string' &&
				tshvalue.startsWith(PUSH_TYPE_PREFIX_SHORT)
			) {
				const channelNum = parseInt(tshvalue.substring(1), 10);
				if (!isNaN(channelNum)) {
					remoteControlChannel = `${CHANNEL_PREFIX}${channelNum}`;
					pushValue = inputReader(PUSH_TYPE_PREFIX_SHORT);
				}
			}
			return {
				name: 'push_type',
				value: pushValue,
				channel,
				input_index: channel,
				...(remoteControlChannel && { remote_control_channel: remoteControlChannel }),
			};
		}
		case 'rotie1':
		case 'rotie1c' /**
		 * Remote control scroll wheel handling (SBRC-005B specific)
		 *
		 * Standard format (rotie1): Scroll wheel rotation detection
		 * Channel-specific format (rotie1c): Scroll with channel targeting
		 *
		 * For channel-encoded values (e.g., "L1", "R1", "L2", "R2"):
		 * - First character: Direction (L=up/left, R=down/right)
		 * - Remaining digits: Remote control's output channel number
		 * - We extract the channel and preserve only the direction for UI display
		 */: {
			const CHANNEL_PREFIX = 'channel_';
			let scrollChannel;
			let scrollValue = tshvalue;

			if (sceneCommand === 'rotie1c' && typeof tshvalue === 'string') {
				const direction = tshvalue.charAt(0);
				const channelNum = parseInt(tshvalue.substring(1), 10);
				if (!isNaN(channelNum)) {
					scrollChannel = `${CHANNEL_PREFIX}${channelNum}`;
					scrollValue = direction;
				}
			}

			return {
				name: 'scroll_position',
				value: scrollValue,
				channel,
				...(scrollChannel && { remote_control_channel: scrollChannel }),
			};
		}
		case 'rotation0':
			return {
				name: 'scroll_steps',
				value: Number(tshvalue),
				channel,
			};
		case 'rotie1s':
			return {
				name: 'scroll_steps',
				value: Number(tshvalue),
				channel,
			};
		case 'rotie1sc': {
			const CHANNEL_PREFIX = 'channel_';
			let stepsValue = Number(tshvalue);
			let scrollStepsChannel;
			if (typeof tshvalue === 'string' && tshvalue.includes('_')) {
				const [numPart, chPart] = tshvalue.split('_');
				const chNum = parseInt(chPart, 10);
				if (!isNaN(chNum)) {
					scrollStepsChannel = `${CHANNEL_PREFIX}${chNum}`;
					stepsValue = Number(numPart);
				}
			}
			return {
				name: 'scroll_steps',
				value: stepsValue,
				channel,
				...(scrollStepsChannel && { remote_control_channel: scrollStepsChannel }),
			};
		}
		case 'm1':
		case 'm2':
		case 'm3':
		case 'm4':
		case 'm5':
		case 'm6':
		case 'm7':
		case 'm8':
			/**
			 * here we return both channel ans meter_ind equal each other
			 * because m1, m2 could stand for 2 channel PM device
			 * but in SHEM-3 case we need meter_ind
			 * and we need both and on the next step, when we already know what is the device type
			 * from UI data, we decide what to do
			 */
			return {
				name: 'meter',
				value: Number(tshvalue),
				channel,

				input_index: channel,
				// add_info_ind: channel
			};
		case 'v1':
		case 'v2':
		case 'v3':
		case 'v4':
			return {
				name: 'vol_meter',
				value: Number(tshvalue),
				channel,

				input_index: channel,
				// add_info_ind: channel
			};
		case 'mt':
			return { name: 'total_current', value: Number(tshvalue), channel };
		case 'm1tariff':
			return { name: 'tariff', value: `T${tshvalue}`, channel, input_index: channel };
		case 'tmp':
		case 'tmp1':
			return {
				name: 'temperature',
				value: Number(tshvalue),
				channel,
			};
		case 't1':
		case 't2':
		case 't3':
			return {
				name: 'temperature',
				value: Number(tshvalue),
				channel,
				add_info_ind: extractAddOnId(sceneCommand),
			};

		case 'hum':
			return {
				name: 'humidity',
				value: Number(tshvalue),
				channel,
			};
		case 'moisture':
		case 'moisture1':
			return {
				name: 'moisture',
				value: Number(tshvalue),
				channel,
			};
		case 'h1':
			return {
				name: 'humidity',
				value: Number(tshvalue),
				channel,
				add_info_ind: extractAddOnId(sceneCommand),
			};
		case 'bat':
			return { name: 'battery', value: Number(tshvalue), channel };
		case 'w':
		case 'white':
			return {
				name: 'white_channel',
				value: percent_from_value_range(Number(tshvalue), [0, 255]),
				channel,
			};
		/**
		 * For now its only one external switch
		 */
		case 'es1':
			return { name: 'ext_switch', value: tshvalue, channel };

		/**res.pos1=nmb(st.thermostats[0].pos); for less than great than */
		case 'pos1':
			return {
				name: 'valve_percent_position',
				value: tshvalue,
				channel,
			};
		case 'isopen1':
			return {
				name: 'valve_open_state',
				value: tshvalue === 'on' ? 'open' : 'closed',
				channel,
			};

		// category PV - Zendure options
		case 'solarInputPower':
			return {
				name: 'solar_input_power',
				value: Number(tshvalue),
				channel,
			};

		case 'outputHomePower':
			return {
				name: 'output_home_power',
				value: Number(tshvalue),
				channel,
			};

		case 'outputLimit':
			return {
				name: 'outputLimit',
				value: Number(tshvalue),
				channel,
			};

		case 'electricLevel':
			return {
				name: 'electricLevel',
				value: Number(tshvalue),
				channel,
			};

		case 'wo1':
			return {
				name: 'report_window_open',
				value: tshvalue,
				channel,
			};
		case 'boost1':
			return { name: 'boost', value: tshvalue, channel };
		case 'sch1':
			return { name: 'set_profile', value: tshvalue, channel };
		case 'tgt1':
			return { name: 'target_temp', value: tshvalue, channel };
		case 'rep':
			/**works only with main device id (ble observer works only with main device and does not take care of channels) */
			return {
				name: 'reported_by',
				value: tshvalue.toLocaleLowerCase().startsWith('x') ? tshvalue : shellyId16(tshvalue),
			};
		case 'bolt_state':
			return {
				name: 'bolt_state',
				value: tshvalue,
				channel,
			};
		case 'mode':
			return {
				name: VENTORA_FAN_SCENE_UI.MODE,
				value: tshvalue,
				channel,
			};
		case 'on':
			return {
				name: VENTORA_FAN_SCENE_UI.ON,
				value: ventoraFanOnCloudToUi(tshvalue),
				channel,
			};
		case 'speed':
			return {
				name: VENTORA_FAN_SCENE_UI.SPEED,
				value: Number(tshvalue),
				channel,
			};
		case 'source':
			return {
				name: VENTORA_FAN_SCENE_UI.SOURCE,
				value: tshvalue,
				channel,
			};
		case 'set_temp':
			return {
				name: 'set_temp',
				value: tshvalue,
				channel,
			};
		case 'temp_enable':
			return {
				name: 'temp_enable',
				value: tshvalue,
				channel,
			};
		// Diffuser (used by both conditions and actions)
		case 'cil':
			return {
				name: 'intensity_left',
				value: Number(tshvalue),
				channel,
			};
		case 'cir':
			return {
				name: 'intensity_right',
				value: Number(tshvalue),
				channel,
			};
		case 'bl':
			return {
				name: tshvalue === false ? 'cury_stop' : 'cury_boost',
				value: tshvalue,
				slot: 'left',
				channel,
			};
		case 'br':
			return {
				name: tshvalue === false ? 'cury_stop' : 'cury_boost',
				value: tshvalue,
				slot: 'right',
				channel,
			};
		case 'onl':
			return {
				name: 'cury_on',
				value: tshvalue,
				slot: 'left',
				channel,
			};
		case 'onr':
			return {
				name: 'cury_on',
				value: tshvalue,
				slot: 'right',
				channel,
			};
		case 'cll':
			return {
				name: 'level_left',
				value: Number(tshvalue),
				channel,
			};
		case 'clr':
			return {
				name: 'level_right',
				value: Number(tshvalue),
				channel,
			};
		case 'ca':
			// Cury ambient configuration
			// tshvalue is an object: {_isdata: true, enable: bool, brightness: number, red: number, green: number, blue: number}
			if (typeof tshvalue === 'object' && tshvalue._isdata) {
				return {
					name: 'cury_ambient',
					value: {
						enable: tshvalue.enable || false,
						brightness: tshvalue.brightness || 0,
						red: tshvalue.red || 0,
						green: tshvalue.green || 0,
						blue: tshvalue.blue || 0,
					},
					channel,
				};
			}
			return {
				name: 'cury_ambient',
				value: tshvalue,
				channel,
			};

		default:
			let add_info_ind,
				name = sceneCommand,
				value = tshvalue;

			// Handle S4SN-0U61X presence zone parameters using helper functions
			if (isPresenceZoneCondition(sceneCommand)) {
				const deviceZoneId = extractZoneIdFromPresenceCondition(sceneCommand);
				const conditionType = getPresenceConditionType(sceneCommand);

				if (deviceZoneId && conditionType) {
					console.log('DEBUG: Reverse mapping presence condition', {
						originalSdr: sceneCommand,
						deviceZoneId,
						conditionType,
						value: tshvalue,
					});

					let uiConditionName: string;
					let resultValue: any;

					if (conditionType === 'state') {
						// Zone state condition (presence/absence)
						uiConditionName = 'presence_zone_state';
						resultValue = normalizePresenceConditionValue(tshvalue, 'state');
					} else if (conditionType === 'delta') {
						// Zone enter/leave delta condition
						uiConditionName = 'presence_zone_objects_delta';
						resultValue = normalizePresenceConditionValue(tshvalue, 'object_count');
					} else if (conditionType === 'object_count') {
						// Zone object count condition
						uiConditionName = 'presence_zone_objects';
						resultValue = normalizePresenceConditionValue(tshvalue, 'object_count');
					} else {
						console.warn('Unknown presence condition type', {
							sceneCommand,
							conditionType,
						});
						return { name: sceneCommand, value: tshvalue, channel: 0 };
					}

					// Validate the result before returning
					const isValid = validatePresenceConditionData(
						{
							conditionName: uiConditionName,
							deviceZoneId: deviceZoneId,
							value: resultValue,
							operator: 'eq', // Default operator for validation
						},
						'utilities_scenes_to_ui.reverse_mapping',
					);

					if (isValid) {
						return {
							name: uiConditionName,
							value: resultValue,
							channel: 0, // Presence sensor has no channels
							add_info_ind: deviceZoneId,
						};
					} else {
						console.error('Reverse mapping validation failed', {
							sceneCommand,
							uiConditionName,
							resultValue,
							deviceZoneId,
						});
					}
				} else {
					console.warn('DEBUG: Failed to parse presence condition', {
						sceneCommand,
						deviceZoneId,
						conditionType,
					});
				}
			}
			if (sceneCommand.match(/t[0-9]{3}/)) {
				name = 'temperature';
				// console.log('temp add on', sceneCommand);
				add_info_ind = Number(sceneCommand.substring(1)) - 1;
				value = Number(tshvalue);
			}
			if (sceneCommand.match(/h[0-9]{3}/)) {
				name = 'humidity';
				add_info_ind = Number(sceneCommand.substring(1)) - 1;
				value = Number(tshvalue);
			}

			if (sceneCommand.match(/ip[0-9]{3}/)) {
				name = 'analog_input_add_on';
				add_info_ind = Number(sceneCommand.substring(2)) - 1;
				value = Number(tshvalue);
			}
			if (sceneCommand.match(/v[0-9]{3}/)) {
				name = 'vol_meter_add_on';
				add_info_ind = Number(sceneCommand.substring(1)) - 1;
				value = Number(tshvalue);
			}
			if (sceneCommand.match(/i[0-9]{3}/)) {
				name = 'digital_input_add_on';
				add_info_ind = Number(sceneCommand.substring(1)) - 1;
			}

			if (sceneCommand.match(/ie[0-9]{3}/)) {
				name = 'push_type_add_on';
				add_info_ind = Number(sceneCommand.substring(2)) - 1;
				value = INPUTS_READERS.find((el) => el.check === tshvalue)?.ui || '';
			}

			if (sceneCommand.match(/r[0-9]{3}/)) {
				name = 'turn_add_on';
				add_info_ind = Number(sceneCommand.substring(1)) - 1;
				value = value;
			}

			return typeof add_info_ind !== 'undefined'
				? {
						name,
						value,
						channel,
						add_info_ind,
					}
				: {
						name,
						value,
						channel,
					};
	}
};

const SCENE_WEATHER_TO_UI = {
	// sc: 'snow',
	// rc: 'rain',
	cc: 'clouds',
	dd: 'wind_direction',
	w: 'wind_speed',
	wf: 'wind_force', //"ww":"wf1" or wgf -> gust
	t: 'temperature',
	h: 'humidity',
	pop: 'chance_precipitation',
};
export const SCENE_WEATHER_VALUE_TO_UI = {
	//cc - clouds
	clear: 'clear_sky', // 0%-10%
	few: 'partly_cloudy', // 11-25%
	scattered: 'mostly_cloudy', // 26-50%
	broken: 'cloudy', // 51-84%
	overcast: 'overcast', // 85-100%

	//new types of clouds
	// clear: 'clear sky', //0%-10%
	// partly: 'partly_cloudy', //11-40%
	// mostly: 'mostly_cloudy', //41-70%
	// cloudy: 'cloudy', //71-90%
	// overcast: 'overcast', //91%-100%

	//widn direction
	N: 'north',
	NNE: 'north-northeast',
	NE: 'northeast',
	ENE: 'east-northeast',
	E: 'east',
	ESE: 'east-southeast',
	SE: 'southeast',
	SSE: 'south-southeast',
	S: 'south',
	SSW: 'south-southwest',
	SW: 'southwest',
	WSW: 'west-southwest',
	W: 'west',
	WNW: 'west-northwest',
	NW: 'northwest',
	NNW: 'north-northwest',
	//wind force wf
	wf0: 'calm',
	wf1: 'light air',
	wf2: 'light breeze',
	wf3: 'gentle breeze',
	wf4: 'moderate breeze',
	wf5: 'fresh breeze',
	wf6: 'strong breeze',
	wf7: 'near gale',
	wf8: 'gale',
	wf9: 'strong gale',
	wf10: 'storm',
	wf11: 'violent storm',
	wf12: 'hurricane',
	// 500: 'light rain',
	// 501: 'moderate rain',
	// 502: 'heavy intensity rain',
	// 503: 'very heavy rain',
	// 600: 'light snow',
	// 601: 'snow',
	// 602: 'heavy snow',
	// 615: 'light rain and snow',
	// 616: 'rain and snow'
};

export const convertSceneWeatherToUi = (sceneCommand, tshvalue) => {
	let cond = SCENE_WEATHER_TO_UI[sceneCommand] || '',
		value = SCENE_WEATHER_VALUE_TO_UI[tshvalue] || tshvalue.toString();

	return {
		cond,
		value,
	};
};

function getCurrencyToMWh(country_val) {
	const countryName = country_val.split('|')[0];

	const components = _init_exchange_based_conditions();

	let energyExchangeComponent = components.find((el) => {
		if (el.cond === 'energy_exchange') {
			return el;
		}
	});

	let energyExchangeCountry = energyExchangeComponent.countries.find((country) => {
		if (country.value === countryName) {
			return country;
		}
	});

	return energyExchangeCountry.character;
}

/**
 * Extracts the id and channel from the provided interface device id string
 * Used to extract the `_gui_device_id` from the scene object
 *
 * @param gui_device_id - Interface device id string. It is in the format of `<device id>_<channel>`
 * @returns {DeviceID} - Object with extracted id and channel
 */
export function extractGuiDeviceId(gui_device_id: string): DeviceID {
	let [device, channel] = gui_device_id.split('_');
	return {
		id: device,
		channel: parseInt(channel) || 0,
	};
}
